/**
 * 백엔드가 만든 DSD 표 HTML(`dangerouslySetInnerHTML`)에 셀 드래그 선택/복사를 붙인다.
 *
 * React 로 셀을 그리는 표(PlainDsdTableView·DiffTableView)는 렌더 시점에
 * `selectableCellAttrs` 로 선택 계약을 세팅하지만, 이 표들은 HTML 문자열이 그대로
 * 주입돼 그럴 기회가 없다. 그래서 주입된 DOM 을 훑어 같은 계약(id·data-cell-id·
 * data-display-value)을 심어준 뒤, 다른 표와 동일한 공통 훅에 위임한다.
 *
 * 사용처: 최종산출물의 "DSD 원본 표", DSD 워크북-IXD 값 채우기의 "DSD 원본보기".
 *
 * 복사값을 `textContent` 에서 만드는 이유: 이 경로에는 셀 값을 담은 프론트 데이터
 * 모델이 없다 — 백엔드 HTML 자체가 원본이라 텍스트가 곧 값이다(칩·마커 같은 표현용
 * 마크업 없이 순수 텍스트로 온다). 데이터 모델이 있는 표에서는 이 훅을 쓰지 말고
 * `selectableCellAttrs` 로 렌더 시점에 값을 싣는다.
 */

import { useCallback, useEffect, useId, useRef, type RefObject } from "react";
import {
  MULTI_SELECT_CELL_BORDER_CSS,
  selectableCellAttrs,
  useTableCellSelection,
} from "@/hooks/use-table-cell-selection";

/** React useId 의 `:r0:` 같은 값은 DOM id 로 쓰기 번거로우므로 단순화한다. */
const toIdToken = (raw: string): string => raw.replace(/[^\w-]/g, "");

/**
 * 백엔드가 **문단** 셀에 붙이는 flag (`PARAGRAPH_FLAGS`, xbrl-back `excel/domain/vo/table.py`).
 * 같은 `<table>` 안에 표 데이터와 섞여 온다.
 *
 * 표 쪽 flag(`note-table`·`body`)를 허용목록으로 두지 않고 문단을 거르는 이유: 표 flag 는
 * 종류가 더 많고(주석 표·본문 그리드) 늘어날 수 있는데, 허용목록이면 **모르는 표 flag 가
 * 통째로 선택 불가**가 된다. 문단은 목록이 닫혀 있어 거르는 쪽이 안전하다.
 * flag 를 아예 안 다는 표(백엔드 렌더러 차이)도 이 규칙이면 자연히 전 셀이 대상이 된다.
 */
const PARAGRAPH_FLAGS = new Set([
  "section",
  "desc",
  "desc-foot",
  "subsection",
]);

/** 백엔드가 주석 표의 데이터 셀에 붙이는 flag. 빈 칸을 남길지 가르는 데만 쓴다. */
const NOTE_TABLE_FLAG = "note-table";

/**
 * 주입된 표 셀에 선택 계약을 심는다. 백엔드가 주는 `data-cell-address`(A1·B3…)를
 * 셀 키로 쓰고, 없으면 행/열 위치로 대체한다. 이미 심어둔 셀은 값만 갱신한다.
 *
 * 문단 셀(`section`·`desc`·`subsection`·`desc-foot`)은 대상에서 뺀다. 이 표들은 설명 문단과
 * 각주도 같은 `<table>` 의 셀로 오는데, 거기서는 드래그 선택보다 브라우저의 텍스트
 * 선택이 맞다 — 계약을 안 심으면 mousedown 이 가로채이지 않아 그대로 동작한다.
 * 문단만으로 이뤄진 표(설명뿐인 주석)도 이 규칙이면 통째로 텍스트 선택에 맡겨진다.
 *
 * 값이 없는 셀은 **표 밖에서만** 뺀다. 이 표들은 문단 사이 간격을 빈 셀 행으로 만들기
 * 때문에, 거기까지 심으면 본문 여백을 클릭했는데 셀이 선택되는 이상한 반응이 된다.
 * 반면 표 안(`note-table`)의 빈 칸은 남긴다 — 미기재 칸이나 좌상단 모서리처럼 빈 칸에서
 * 드래그를 시작하는 게 자연스럽고, 한 칸만 집어 복사하는 것도 되어야 한다.
 */
export const decorateRawDsdCells = (
  root: HTMLElement,
  idPrefix: string,
): void => {
  const tables = root.querySelectorAll("table");
  tables.forEach((table, tableIndex) => {
    const cells = table.querySelectorAll<HTMLTableCellElement>("td, th");
    cells.forEach((cell, cellIndex) => {
      const flag = cell.getAttribute("data-flag") ?? "";
      if (PARAGRAPH_FLAGS.has(flag)) return;
      const displayValue = cell.textContent?.trim() ?? "";
      // 표 안의 빈 칸은 남기고, 표 밖(문단 사이 여백) 빈 셀만 뺀다.
      if (displayValue === "" && flag !== NOTE_TABLE_FLAG) return;
      const address = cell.getAttribute("data-cell-address") ?? `i${cellIndex}`;
      const cellId = `${idPrefix}-t${tableIndex}-${address}`;
      const attrs = selectableCellAttrs({
        uniqueId: cellId,
        cellId,
        displayValue,
      });
      for (const [name, value] of Object.entries(attrs)) {
        if (name === "id") cell.id = value;
        else cell.setAttribute(name, value);
      }
    });
  });
};

/**
 * @param containerRef 표 HTML 이 주입되는 컨테이너(스크롤 루트여도 된다)
 * @param styleId 선택 강조 `<style>` 의 id — 화면별로 겹치지 않게 분리한다
 */
export function useRawDsdTableSelection(
  containerRef: RefObject<HTMLDivElement | null>,
  styleId: string,
) {
  const instanceId = toIdToken(useId());
  const idPrefix = `raw-dsd-cell-${instanceId}`;
  const idPrefixRef = useRef(idPrefix);
  idPrefixRef.current = idPrefix;

  const decorate = useCallback(() => {
    const root = containerRef.current;
    if (root) decorateRawDsdCells(root, idPrefixRef.current);
  }, [containerRef]);

  // HTML 은 쿼리 응답·토글로 나중에 주입되므로, 호출부가 의존성을 챙기지 않아도
  // 되도록 DOM 변화를 직접 관찰한다. 속성은 관찰하지 않는다 — 이 훅이 심는 속성
  // 변경이 다시 콜백을 부르는 순환을 피하기 위함.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    decorate();
    const observer = new MutationObserver(decorate);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [containerRef, decorate]);

  useTableCellSelection(containerRef, {
    styleId,
    autoBindContainer: true,
    // 이 표들은 헤더 행도 td 로 오지만, 병합 셀은 흔하므로 그리드 범위 선택을 켠다.
    enableGridRangeSelection: true,
    clipboardTableFormat: "excel-table",
    enableArrowNavigation: false,
    selectionCss: MULTI_SELECT_CELL_BORDER_CSS,
    // minSelectionToStyle 은 기본값(1) — 클릭 한 번에 그 셀만 선택·복사된다.
    // 대사·리뷰 화면은 단일 클릭을 기존 액션(네비게이터·리뷰 선택)이 소유해서 2 로
    // 양보하지만, 이 표들은 읽기 전용이라 클릭이 비어 있어 양보할 이유가 없다.
  });
}
